import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  I18N_CONFIG,
  isPageTranslated,
  isRTL,
  getLanguageFromPath,
  getBasePath,
  buildLanguageUrl,
  getAvailableLanguages,
  SupportedLanguage,
} from "../config/i18n";

interface I18nContextType {
  currentLanguage: string;
  changeLanguage: (lng: string) => void;
  isRTL: boolean;
  availableLanguages: Array<{
    code: string;
    name: string;
    nativeName: string;
    isAvailable: boolean;
  }>;
  isCurrentPageTranslated: boolean;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

interface I18nProviderProps {
  children: ReactNode;
}

// All helper functions are now imported from config/i18n.ts

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || "en");
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize language from URL on mount
  useEffect(() => {
    if (!isInitialized) {
      const urlLanguage = getLanguageFromPath(location.pathname);
      setCurrentLanguage(urlLanguage);
      i18n.changeLanguage(urlLanguage);
      setIsInitialized(true);
    }
  }, [location.pathname, i18n, isInitialized]);

  // Update language when URL changes (but only after initialization)
  useEffect(() => {
    if (isInitialized) {
      const urlLanguage = getLanguageFromPath(location.pathname);
      if (urlLanguage !== currentLanguage) {
        setCurrentLanguage(urlLanguage);
        i18n.changeLanguage(urlLanguage);
      }
    }
  }, [location.pathname, currentLanguage, i18n, isInitialized]);

  // Check if current page has translations for current language
  const basePath = getBasePath(location.pathname);
  const isCurrentPageTranslated = isPageTranslated(
    basePath,
    currentLanguage as SupportedLanguage
  );

  // Generate available languages based on current page
  const updatedAvailableLanguages = getAvailableLanguages(basePath);

  // Check if current language is RTL
  const isCurrentLanguageRTL = isRTL(currentLanguage);

  useEffect(() => {
    // Update document direction and language
    document.documentElement.dir = isCurrentLanguageRTL ? "rtl" : "ltr";
    document.documentElement.lang = currentLanguage;

    // Update body class for RTL styling
    if (isCurrentLanguageRTL) {
      document.body.classList.add("rtl");
    } else {
      document.body.classList.remove("rtl");
    }

    // Update document title based on language
    if (currentLanguage === "fa") {
      document.title = "نیومی مپر - ایجاد ایالت‌ها در خاورمیانه";
    } else {
      document.title = "NewME Mapper - Create States in Middle East";
    }
  }, [currentLanguage, isCurrentLanguageRTL]);

  // If user switches to a language but current page doesn't have translations, switch back to default
  useEffect(() => {
    if (
      currentLanguage !== I18N_CONFIG.defaultLanguage &&
      !isCurrentPageTranslated
    ) {
      setCurrentLanguage(I18N_CONFIG.defaultLanguage);
      i18n.changeLanguage(I18N_CONFIG.defaultLanguage);
      localStorage.setItem("i18nextLng", I18N_CONFIG.defaultLanguage);
    }
  }, [currentLanguage, isCurrentPageTranslated, i18n]);

  const changeLanguage = (lng: string) => {
    // Only allow switching to a language if current page has translations
    if (
      lng !== I18N_CONFIG.defaultLanguage &&
      !isPageTranslated(basePath, lng as SupportedLanguage)
    ) {
      return; // Don't change language
    }

    // Build new URL with language prefix
    const newUrl = buildLanguageUrl(
      location.pathname,
      lng as SupportedLanguage
    );

    // Update language state
    i18n.changeLanguage(lng);
    setCurrentLanguage(lng);
    localStorage.setItem("i18nextLng", lng);

    // Navigate to new URL
    navigate(newUrl);
  };

  const value: I18nContextType = {
    currentLanguage,
    changeLanguage,
    isRTL: isCurrentLanguageRTL,
    availableLanguages: updatedAvailableLanguages,
    isCurrentPageTranslated,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error("useI18n must be used within an I18nProvider");
  }
  return context;
};
